import { useAtomValue } from 'jotai';
import { useCallback, useEffect, useRef } from 'react';
import { IConfig } from '../../config';
import { appAuthAtom, inventoryDetailsAtom } from '../../state';
import { PositionChanged } from '../../types';

export type UseUserStreamPortfolioProps = {
  config: IConfig;
  isPrimaryInstance: boolean;
  loadCollateralSummary: (force: boolean) => Promise<void>;
  loadInventoryDetails: () => Promise<void>;
  parseAndSavePortfolio: (portfolio: PositionChanged) => Promise<void>;
};

/**
 * Force load inventory details if it's not loaded after a certain time through the user stream
 */
export const useUserStreamPortfolioLoader = ({
  config,
  isPrimaryInstance,
  loadInventoryDetails,
  parseAndSavePortfolio,
  loadCollateralSummary,
}: UseUserStreamPortfolioProps) => {
  const initPortfolioTimeout = useRef<number>(undefined);
  const authState = useAtomValue(appAuthAtom);
  const inventoryDetails = useAtomValue(inventoryDetailsAtom);

  const clearPortfolioInitTimeout = () => {
    if (initPortfolioTimeout.current) {
      clearTimeout(initPortfolioTimeout.current);
    }
  };

  useEffect(() => {
    if (authState.isAuthenticated && inventoryDetails.isLoading && isPrimaryInstance) {
      clearPortfolioInitTimeout();
      initPortfolioTimeout.current = setTimeout(() => {
        if (inventoryDetails.isLoading) {
          loadInventoryDetails();
        }
      }, config.NetBroker.Timeouts.ForceLoadPortfolioTimeout) as unknown as number;
    }
    if (!authState.isAuthenticated || !inventoryDetails.isLoading) {
      clearPortfolioInitTimeout();
    }

    return () => {
      clearPortfolioInitTimeout();
    };
  }, [authState, inventoryDetails, loadInventoryDetails, isPrimaryInstance]);

  return useCallback(
    async (positionChanged: PositionChanged) => {
      parseAndSavePortfolio(positionChanged);
      loadCollateralSummary(true);
    },
    [parseAndSavePortfolio, loadCollateralSummary],
  );
};
